/** 확대 단계(V10) — 맞춤이 가장 작고 400% 가 가장 크다. 100% = 렌더 결과 1px 당 CSS 1px. */
export const ZOOM_LEVELS = ['fit', 1, 1.5, 2, 3, 4] as const;
export type ZoomLevel = (typeof ZOOM_LEVELS)[number];
type ZoomStep = Exclude<ZoomLevel, 'fit'>;

export interface ZoomPanSize {
  readonly width: number;
  readonly height: number;
}

export interface ZoomPanState {
  readonly level: ZoomLevel;
  /** 지금 배율(CSS px ÷ 렌더 px). 맞춤이면 fitScale. */
  readonly scale: number;
  /** 무대에 그림 전체가 들어가는 배율. 크기를 모르면 1. */
  readonly fitScale: number;
  readonly viewport: ZoomPanSize;
  readonly content: ZoomPanSize;
  /** 무대 가운데에서 그림 가운데까지의 이동(CSS px). */
  readonly offsetX: number;
  readonly offsetY: number;
}

export type ZoomPanAction =
  | { readonly type: 'zoomIn' }
  | { readonly type: 'zoomOut' }
  | { readonly type: 'fit' }
  | { readonly type: 'pan'; readonly dx: number; readonly dy: number }
  | { readonly type: 'resize'; readonly viewport: ZoomPanSize; readonly content: ZoomPanSize };

const EMPTY: ZoomPanSize = { width: 0, height: 0 };

export const INITIAL_ZOOM_PAN: ZoomPanState = {
  level: 'fit',
  scale: 1,
  fitScale: 1,
  viewport: EMPTY,
  content: EMPTY,
  offsetX: 0,
  offsetY: 0,
};

const STEPS: readonly ZoomStep[] = ZOOM_LEVELS.filter(
  (level): level is ZoomStep => level !== 'fit',
);
/** 소수 비교 여유 — 맞춤이 어떤 단계와 같으면 그 단계를 건너뛴다. */
const EPSILON = 1e-6;

const fitScaleOf = (viewport: ZoomPanSize, content: ZoomPanSize): number =>
  viewport.width > 0 && viewport.height > 0 && content.width > 0 && content.height > 0
    ? Math.min(viewport.width / content.width, viewport.height / content.height)
    : 1;

const sameSize = (a: ZoomPanSize, b: ZoomPanSize): boolean =>
  a.width === b.width && a.height === b.height;

const scaleOf = (state: ZoomPanState, level: ZoomLevel): number =>
  level === 'fit' ? state.fitScale : level;

/** 그림이 무대를 넘친 만큼의 절반까지만 움직인다 — 가장자리에서 멈추고, 작은 축은 가운데에 둔다. */
const clampAxis = (offset: number, viewport: number, content: number, scale: number): number => {
  const limit = Math.max(0, (content * scale - viewport) / 2);
  const clamped = Math.min(limit, Math.max(-limit, offset));
  // -0 을 0 으로 — 상태 비교가 흔들리지 않게 한다.
  return clamped === 0 ? 0 : clamped;
};

const place = (
  state: ZoomPanState,
  level: ZoomLevel,
  offsetX: number,
  offsetY: number,
): ZoomPanState => {
  const scale = scaleOf(state, level);
  return {
    ...state,
    level,
    scale,
    offsetX: clampAxis(offsetX, state.viewport.width, state.content.width, scale),
    offsetY: clampAxis(offsetY, state.viewport.height, state.content.height, scale),
  };
};

/** 무대 가운데를 기준으로 확대한다 — 가운데에 보이던 곳이 남도록 이동량도 같은 비율로 바꾼다. */
const zoomTo = (state: ZoomPanState, level: ZoomLevel): ZoomPanState => {
  const scale = scaleOf(state, level);
  return place(
    state,
    level,
    (state.offsetX * scale) / state.scale,
    (state.offsetY * scale) / state.scale,
  );
};

const levelAbove = (state: ZoomPanState): ZoomStep | undefined =>
  STEPS.find((step) => step > state.scale + EPSILON);

const levelBelow = (state: ZoomPanState): ZoomLevel | undefined => {
  if (state.level === 'fit') return undefined;
  return (
    STEPS.findLast((step) => step < state.scale - EPSILON && step > state.fitScale + EPSILON) ??
    'fit'
  );
};

/** 확대·이동 상태(V10). 바뀐 것이 없으면 같은 상태를 돌려준다(재렌더를 줄인다). */
export const zoomPanReducer = (state: ZoomPanState, action: ZoomPanAction): ZoomPanState => {
  switch (action.type) {
    case 'zoomIn': {
      const level = levelAbove(state);
      return level === undefined ? state : zoomTo(state, level);
    }
    case 'zoomOut': {
      const level = levelBelow(state);
      return level === undefined ? state : zoomTo(state, level);
    }
    case 'fit':
      return state.level === 'fit' && state.offsetX === 0 && state.offsetY === 0
        ? state
        : place(state, 'fit', 0, 0);
    case 'pan':
      return place(state, state.level, state.offsetX + action.dx, state.offsetY + action.dy);
    case 'resize': {
      if (sameSize(state.viewport, action.viewport) && sameSize(state.content, action.content))
        return state;
      const fitScale = fitScaleOf(action.viewport, action.content);
      const resized: ZoomPanState = {
        ...state,
        viewport: action.viewport,
        content: action.content,
        fitScale,
      };
      // 맞춤이 가장 작다 — 새 맞춤 이하가 된 단계는 맞춤으로 올린다.
      const level =
        state.level !== 'fit' && state.level <= fitScale + EPSILON ? 'fit' : state.level;
      return place(resized, level, state.offsetX, state.offsetY);
    }
  }
};
